import { ChatAdapter } from './ChatAdapter';
import { createConsumer } from '@rails/actioncable';

const CHANNEL = 'Pibox::DeliveryRoute::Chat::Channel';

// The route chat says author_side; the booking chat render reads sender.
const SENDER_BY_SIDE = { company: 0, driver: 1, picap: 2 };

/**
 * DeliveryRouteChatAdapter - chat entre el conductor de una ruta de entrega y la compañía
 * dueña de UN prepaquete de esa ruta (Pibox::DeliveryRoute::Chat::Channel en picap_api).
 *
 * Habla con la misma interfaz que GrpcChatAdapter para que BookingChat no sepa cuál usa,
 * pero está atado a UN hilo (ruta + prepaquete): el primer argumento de cada método
 * (bookingId en el chat de booking) se acepta por compatibilidad y se ignora.
 *
 * Grano: un hilo por PREPAQUETE, no por compañía (specs/007-chat-por-paquete/). Dos
 * paquetes de la misma compañía en la misma ruta son dos suscripciones distintas — cuál
 * está abierta la decide DeliveryRouteChatList, este adaptador solo habla con la que se le
 * pidió.
 *
 * Diferencias con el chat de booking:
 * - El servidor transmite el historial solo al confirmar la suscripción; no se pide.
 * - `create_message` trae UN mensaje en data.message, no la conversación entera.
 * - El lado (compañía / conductor / picap) lo decide el servidor; el cliente no lo manda.
 * - No hay fallback a Firebase: no existe historial anterior de una ruta.
 */
export class DeliveryRouteChatAdapter extends ChatAdapter {
  constructor({ deliveryRouteId, prePackageId }) {
    super();
    this.deliveryRouteId = deliveryRouteId;
    this.prePackageId = prePackageId;

    this.wsUrl = process.env.REACT_APP_GRPC_URL || 'https://api.picap.app/cable';
    this.consumer = null;
    this.subscription = null;

    this.messageCallbacks = [];
    this.writingCallbacks = [];
    this.messageIds = new Set();
    this.writingTimeout = null;
    this.handlers = {};
  }

  /**
   * Handlers de estado del hilo que el componente traduce a UI.
   * @param {{onRejected?: Function, onClosed?: Function, onError?: Function}} handlers
   */
  setHandlers = (handlers) => {
    this.handlers = { ...this.handlers, ...handlers };
  }

  getAuthToken = () => {
    try {
      return localStorage.getItem('session_token') || null;
    } catch (error) {
      return null;
    }
  }

  initialize = () => {
    if (this.consumer) {
      return this.consumer;
    }

    const token = this.getAuthToken();
    this.consumer = createConsumer(token ? `${this.wsUrl}?t=${token}` : this.wsUrl);
    return this.consumer;
  }

  listenForMessages = (_key, onMessageReceived) => {
    this.messageCallbacks.push(onMessageReceived);
    if (this.subscription) {
      return;
    }

    this.subscription = this.initialize().subscriptions.create(
      { channel: CHANNEL, delivery_route_id: this.deliveryRouteId, pre_package_id: this.prePackageId },
      {
        connected: () => {},
        disconnected: () => {},
        rejected: () => this.notify('onRejected'),
        received: this.handleReceived,
      }
    );
  }

  listenLastMessages = (key, onMessageReceived) => {
    this.listenForMessages(key, onMessageReceived);
  }

  listenForWriting = (_key, onWritingChange) => {
    this.writingCallbacks.push(onWritingChange);
  }

  handleReceived = (data) => {
    const payload = data.data || {};

    switch (data.method) {
      case 'history':
        this.handleHistory(payload.conversation || {});
        break;
      case 'create_message':
        this.deliver(payload.message);
        break;
      case 'writing':
        this.handleWriting((payload.conversation || {}).writing || {});
        break;
      case 'error':
        payload.error === 'closed' ? this.notify('onClosed') : this.notify('onError', payload.error);
        break;
      default:
        // read, badge: no los pinta este componente
        break;
    }
  }

  // The server sends the newest first.
  handleHistory = (conversation) => {
    [...(conversation.messages || [])].reverse().forEach(this.deliver);
    if (conversation.writable === false) {
      this.notify('onClosed');
    }
  }

  deliver = (raw) => {
    if (!raw) {
      return;
    }
    if (raw.id) {
      if (this.messageIds.has(raw.id)) {
        return;
      }
      this.messageIds.add(raw.id);
    }

    const message = this.normalize(raw);
    this.messageCallbacks.forEach((callback) => {
      try {
        callback(message);
      } catch (error) {
        // un callback roto no debe frenar a los demás
      }
    });
  }

  normalize = (raw) => ({
    id: raw.id,
    message: raw.content,
    sender: SENDER_BY_SIDE[raw.author_side],
    createdAt: raw.created_at || Date.now(),
    fileUrl: raw.file_url,
    fileName: raw.file_name,
    fileKind: raw.file_kind,
    fileContentType: raw.file_content_type,
    coordinates: raw.coordinates,
    subjectPrepackageId: raw.subject_prepackage_id,
  })

  handleWriting = (writing) => {
    const userId = Object.keys(writing)[0] || null;
    const status = { isWriting: userId !== null, message: userId ? writing[userId] : null, userId };
    this.writingCallbacks.forEach((callback) => {
      try {
        callback(status);
      } catch (error) {
        // ver deliver
      }
    });
  }

  notify = (name, arg) => {
    const handler = this.handlers[name];
    if (handler) {
      handler(arg);
    }
  }

  /**
   * @param {string} message
   * @param {*} _senderType ignorado: el lado lo decide el servidor
   * @param {string} _key ignorado
   */
  sendMessage = async (message, _senderType, _key) => {
    if (!this.subscription) {
      return { success: false, error: 'No active WebSocket subscription. Please wait for connection.' };
    }

    const data = {
      // Numérico: el servidor lo usa para descartar reenvíos del mismo mensaje.
      uid: Date.now() * 1000 + Math.floor(Math.random() * 1000),
      content: message,
      file_kind: 'html',
    };

    this.subscription.perform('create_message', { data });
    return { success: true, message };
  }

  sendReadReceipt = (_key, messageId) => {
    if (!messageId || !this.subscription) {
      return;
    }
    this.subscription.perform('read', { data: { id: messageId } });
  }

  startWriting = () => {
    if (!this.subscription) {
      return;
    }
    clearTimeout(this.writingTimeout);
    this.subscription.perform('writing', { data: { writing: true } });
    this.writingTimeout = setTimeout(this.stopWriting, 2000);
  }

  stopWriting = () => {
    if (!this.subscription) {
      return;
    }
    clearTimeout(this.writingTimeout);
    this.writingTimeout = null;
    this.subscription.perform('writing', { data: { writing: false } });
  }

  // No hay fallback a Firebase en el chat de ruta.
  isUsingFirebaseFallback = () => false

  disconnect = () => {
    clearTimeout(this.writingTimeout);
    this.writingTimeout = null;
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
    this.messageCallbacks = [];
    this.writingCallbacks = [];
    this.messageIds.clear();
  }
}

export default DeliveryRouteChatAdapter;
